/**
 * Start view, cover thumbnail and hover-preview video.
 *
 * The three pieces of per-scene media the viewer bundle carries besides the
 * splat itself: the camera pose the viewer opens on, a JPEG of it for scene
 * cards, and a short MP4 rendered from the camera tour for hover-play. All
 * three ride the editor-meta sidecar and the export bundle (see doc-meta.ts
 * and scene-export.ts).
 *
 * Rendering goes through the upstream offscreen paths — `render.offscreen`
 * for the thumbnail and `render.video` for the preview — so it stays exact
 * with what File ▸ Render produces; the video is captured into memory with
 * MemoryWritableStream instead of a file.
 */

import { Vec3 } from 'playcanvas';

import type { Events } from '../events';
import { i18n } from '../ui/localization';
import type { VideoSettings } from '../video-config';
import { MemoryWritableStream } from './memory-stream';

export type StartPose = { position: number[]; target: number[]; fov: number };

type LivePose = {
    position: { x: number; y: number; z: number };
    target: { x: number; y: number; z: number };
    fov: number;
};

const dataUrlToBytes = (dataUrl: string): Uint8Array | null => {
    const comma = dataUrl.indexOf(',');
    if (comma < 0) return null;
    const bin = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
};

const registerStartView = (events: Events) => {
    // ── start view ────────────────────────────────────────────────────────
    let startPose: StartPose | null = null;

    const setStartPose = (pose: StartPose | null) => {
        startPose = pose;
        events.fire('camera.startPose', startPose);
    };

    events.function('camera.getStartPose', () => startPose);

    events.on('camera.setStartPose', () => {
        const live = events.invoke('camera.getPose') as LivePose | undefined;
        if (!live) return;
        setStartPose({
            position: [live.position.x, live.position.y, live.position.z],
            target: [live.target.x, live.target.y, live.target.z],
            fov: live.fov
        });
    });

    events.on('camera.clearStartPose', () => setStartPose(null));

    // restore from a document / sidecar (see doc-meta.ts)
    events.on('camera.setStartPoseData', (pose: Partial<StartPose> | null) => {
        if (!pose || !Array.isArray(pose.position) || !Array.isArray(pose.target)) {
            setStartPose(null);
            return;
        }
        setStartPose({
            position: pose.position.slice(0, 3),
            target: pose.target.slice(0, 3),
            fov: typeof pose.fov === 'number' ? pose.fov : (events.invoke('camera.fov') as number ?? 60)
        });
    });

    // used by the walk tool when the floor pick re-levels the whole scene
    events.on('camera.transformStartPose', (fn: (a: number[]) => number[]) => {
        if (!startPose) return;
        setStartPose({
            position: fn(startPose.position),
            target: fn(startPose.target),
            fov: startPose.fov
        });
    });

    events.on('camera.gotoStartPose', () => {
        if (!startPose) return;
        events.fire('camera.setPose', {
            position: new Vec3(startPose.position[0], startPose.position[1], startPose.position[2]),
            target: new Vec3(startPose.target[0], startPose.target[1], startPose.target[2]),
            fov: startPose.fov
        }, 1);
    });

    // ── thumbnail ─────────────────────────────────────────────────────────
    let thumbnailDataUrl: string | null = null;

    events.function('thumbnail.get', () => thumbnailDataUrl);
    events.function('thumbnail.getBytes', (): Uint8Array | null => {
        return thumbnailDataUrl ? dataUrlToBytes(thumbnailDataUrl) : null;
    });

    // Render the current view offscreen at the viewport's aspect, longest side
    // `maxSize`, composited over the background colour and encoded as JPEG.
    events.function('render.thumbnail', async (maxSize = 640): Promise<string | null> => {
        const target = events.invoke('targetSize') as { width: number; height: number } | undefined;
        const aspect = (target?.width ?? 16) / (target?.height ?? 9);
        const width = aspect >= 1 ? maxSize : Math.max(1, Math.round(maxSize * aspect));
        const height = aspect >= 1 ? Math.max(1, Math.round(maxSize / aspect)) : maxSize;
        try {
            const rgba = await events.invoke('render.offscreen', width, height) as Uint8Array;
            const bg = events.invoke('bgClr') as { r: number; g: number; b: number };
            const br = bg.r * 255;
            const bgg = bg.g * 255;
            const bb = bg.b * 255;
            const out = new Uint8ClampedArray(width * height * 4);
            for (let i = 0; i < width * height; i++) {
                const o = i * 4;
                const a = rgba[o + 3] / 255;
                // the offscreen target is cleared transparent and holds
                // premultiplied colour: composite over the editor background
                out[o] = rgba[o] + br * (1 - a);
                out[o + 1] = rgba[o + 1] + bgg * (1 - a);
                out[o + 2] = rgba[o + 2] + bb * (1 - a);
                out[o + 3] = 255;
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;
            ctx.putImageData(new ImageData(out, width, height), 0, 0);
            return canvas.toDataURL('image/jpeg', 0.85);
        } catch (err) {
            console.error('[render.thumbnail]', err);
            return null;
        }
    });

    events.function('thumbnail.capture', async (): Promise<boolean> => {
        const url = await events.invoke('render.thumbnail', 640) as string | null;
        if (!url) return false;
        thumbnailDataUrl = url;
        events.fire('thumbnail.changed', thumbnailDataUrl);
        return true;
    });

    events.on('thumbnail.clear', () => {
        thumbnailDataUrl = null;
        events.fire('thumbnail.changed', null);
    });

    // ── preview (hover) video ─────────────────────────────────────────────
    let previewVideoBytes: Uint8Array | null = null;

    events.function('render.previewVideo', async (): Promise<boolean> => {
        const poses = (events.invoke('camera.poses') as unknown[] | undefined) ?? [];
        const frames = events.invoke('timeline.frames') as number;
        if (poses.length < 2 || !frames || frames < 1) {
            await events.invoke('showPopup', {
                type: 'error',
                header: i18n.t('ollo.preview.no-tour-header'),
                message: i18n.t('ollo.preview.no-tour')
            });
            return false;
        }
        const settings: VideoSettings = {
            startFrame: 0,
            endFrame: frames,
            frameRate: 30,
            width: 1280,
            height: 720,
            bitrate: 4_000_000,
            transparentBg: false,
            showDebug: false,
            format: 'mp4',
            codec: 'h264'
        };
        const stream = new MemoryWritableStream();
        const ok = await events.invoke('render.video', settings, stream);
        if (!ok) return false;
        previewVideoBytes = stream.bytes;
        events.fire('timeline.setFrame', 0);
        await events.invoke('thumbnail.capture');
        events.fire('preview.changed', true);
        return true;
    });

    events.function('preview.getBytes', (): Uint8Array | null => previewVideoBytes);
    events.function('preview.get', (): boolean => !!previewVideoBytes);
    events.on('preview.clear', () => {
        previewVideoBytes = null;
        events.fire('preview.changed', false);
    });

    // a new or replaced document starts with none of the three
    events.on('scene.clear', () => {
        setStartPose(null);
        thumbnailDataUrl = null;
        events.fire('thumbnail.changed', null);
        previewVideoBytes = null;
        events.fire('preview.changed', false);
    });
};

export { registerStartView };
